import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Card, Dropdown, Grid, Icon, Spinner, Text } from "~/components-shared";
import { IntegrationTypeEnum, StatusStateEnum } from "~/types/enums";
import moment from "moment";
import { usePostHog } from "posthog-js/react";
import styled, { useTheme } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import toastsActionCreators from "~/store/toasts/actions";
import integrationSelectors from "~/store/integrations/selectors";
import topicSelectors from "~/store/topics/selectors";
import topicsActionCreators from "~/store/topics/actions";
import { TopicActionEnum } from "~/store/topics/types";
const StyledGrid = styled(Grid)`
  border-radius: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.shape.borderRadius.primary;
}};
`;
const TopicReportInfoCard = _ref2 => {
  let {
    ...rest
  } = _ref2;
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const posthog = usePostHog();
  const sendStatus = useSelector(topicSelectors.getSendStatus);
  const subscribed = useSelector(topicSelectors.getSubscribed);
  const subscribeStatus = useSelector(topicSelectors.getSubscribeStatus);
  const subscribeError = useSelector(topicSelectors.getSubscribeError);
  const [hoverSubscribe, setHoverSubscribe] = useState(false);
  const scheduledDate = moment.utc("4:00pm", "hh:mma").weekday(4).local();
  const deliveryDate = moment().valueOf() > scheduledDate.valueOf() ? scheduledDate.add("7", "days") : scheduledDate; // Friday 10:00am
  const slackIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.SLACK));
  const sendError = useSelector(topicSelectors.getSendError);
  const error = sendError || subscribeError;
  useEffect(() => {
    if (slackIntegration && subscribeStatus === StatusStateEnum.IDLE) {
      dispatch(topicsActionCreators.fetchSubscriptionStarted());
    }
  }, [dispatch, subscribeStatus, slackIntegration]);
  const handleSend = callback => {
    dispatch(topicsActionCreators.sendSlackStarted(_ref3 => {
      let {
        data
      } = _ref3;
      if (data) {
        dispatch(toastsActionCreators.pushed({
          severity: "success",
          message: t("TopicReportInfoCard.reportSent"),
          timeout: 10000
        }));
        callback();
      }
    }));
    posthog.capture(`Topic Report – Send in Slack`);
  };
  const handleSubscribe = () => {
    if (subscribed) {
      dispatch(topicsActionCreators.unsubscribeStarted());
      posthog.capture(`Topic Report – Unsubscribe`);
    } else {
      dispatch(topicsActionCreators.subscribeStarted());
      posthog.capture(`Topic Report – Subscribe`);
    }
  };
  const handleShareInSlack = () => {
    dispatch(topicsActionCreators.actionChanged(TopicActionEnum.SHARE_IN_SLACK, null));
    posthog.capture(`Topic Report – Share in Slack`);
  };
  const gridTemplate = ["auto", "auto", "auto"];
  if (slackIntegration) {
    gridTemplate.push("160px", "2.7rem");
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 6
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(Card, _extends({
    color: "transparent",
    border: true
  }, rest), /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 8,
    gap: 0,
    template: gridTemplate.join(" "),
    s: {
      pl: 6,
      pr: 4,
      py: 3
    },
    align: "stretch"
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2.5
    }
  }, t("TopicReportInfoCard.reportType")), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "bold",
    fontSize: 4
  }, t("TopicReportInfoCard.weeklyTopicReport"))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2.5
    }
  }, t("TopicReportInfoCard.nextDeliveryDate")), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "bold",
    fontSize: 4
  }, deliveryDate.format("ddd, MMM Do [at] h:mma"))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2.5
    }
  }, t("TopicReportInfoCard.deliveryChannel")), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "bold",
    fontSize: 4
  }, slackIntegration ? "Slack" : "Joyn")), slackIntegration && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 1.5
    }
  }, t("TopicReportInfoCard.receiveInSlack")), /*#__PURE__*/React.createElement(Button, {
    onClick: handleSubscribe,
    onMouseEnter: () => setHoverSubscribe(true),
    onMouseLeave: () => setHoverSubscribe(false),
    loading: subscribeStatus === StatusStateEnum.LOADING,
    fontSize: 3,
    s: {
      pl: 0,
      pr: 2,
      py: 1.5
    },
    color: subscribed ? "translucent" : "primary",
    maxWidth: "140px",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Icon, {
    src: subscribed ? "tick" : "bell",
    s: {
      mr: 1,
      mt: -0.5
    }
  }), subscribed ? hoverSubscribe ? t("TopicReportInfoCard.unsubscribe") : t("TopicReportInfoCard.subscribed") : t("TopicReportInfoCard.subscribe"))), /*#__PURE__*/React.createElement(Grid.Cell, {
    align: "center"
  }, slackIntegration && /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    display: "inline-block",
    handlerProps: {
      s: {
        p: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options"),
      color: "translucent"
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      src: "dots-vertical",
      color: colors.text.primary,
      display: "block"
    }),
    fullWidth: true
  }, _ref4 => {
    let {
      isOpen,
      onItemClick
    } = _ref4;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleSend(() => {
          onItemClick();
        });
      }
    }, sendStatus === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }) : /*#__PURE__*/React.createElement(Icon, {
      src: "send",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), t("TopicReportInfoCard.sendToSlack")), /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleShareInSlack();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "share",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), t("TopicReportInfoCard.shareInSlack")));
  }))))));
};
export default TopicReportInfoCard;